<template>
	<!-- 人员配置弹窗 -->
	<el-dialog title="人员配置" center :visible.sync="dialogVisible" :close-on-click-modal="false" :close-on-press-escape="false" width="600px">
		<el-form ref="form" :model="form" :rules="rules" label-width="100px" size="small">
			<!-- 签约级别 -->
			<el-form-item label="签约级别" prop="contractLevelIdList">
				<el-checkbox-group v-model="form.contractLevelIdList">
					<el-checkbox v-for="item in signLevelList" :key="item.id" :label="item.id">
						{{ item.name }}
					</el-checkbox>
				</el-checkbox-group>
			</el-form-item>
			<!-- 角色选择：支持多选、远程搜索 -->
			<el-form-item label="角色选择" prop="roleIdList">
				<el-select
					v-model="form.roleIdList"
					placeholder="请输入角色名称搜索"
					multiple
					filterable
					remote
					reserve-keyword
					:remote-method="remoteSearchRole"
					:loading="roleLoading"
					@focus="handleSelectFocus">
					<el-option v-for="role in roleList" :key="role.id" :label="role.name" :value="role.id" />
				</el-select>
			</el-form-item>
		</el-form>

		<!-- 弹窗底部按钮 -->
		<span slot="footer" class="dialog-footer">
			<el-button @click="handleClose" :disabled="submitLoading">取 消</el-button>
			<el-button type="primary" @click="handleSubmit" :loading="submitLoading">确 定</el-button>
		</span>
	</el-dialog>
</template>

<script>
/**
 * 人员配置弹窗组件
 * 功能：配置技能的签约级别和角色
 * 特性：表单验证、远程搜索、防重复提交
 */
import { debounce } from "@/utils/debounce";
import { sysRolePage, sysDicDataPageList, courseSkillScopeAdd, courseSkillScopeInfo } from "@/api/skill";

export default {
	name: "UserConfig",

	data() {
		return {
			dialogVisible: false, // 弹窗显示状态
			signLevelList: [], // 签约级别列表
			roleList: [], // 角色列表
			roleLoading: false, // 角色搜索loading状态
			submitLoading: false, // 提交按钮loading，防止重复提交
			form: {
				skillId: "", // 技能ID
				contractLevelIdList: [], // 签约级别ID列表
				roleIdList: [], // 角色ID列表
			},
			// 表单验证规则
			rules: {
				contractLevelIdList: [{ required: true, message: "请选择签约级别", trigger: "change" }],
				roleIdList: [{ required: true, message: "请选择角色", trigger: "change" }],
			},
		};
	},

	methods: {
		/**
		 * 初始化弹窗
		 * @param {string} skillId - 技能ID
		 */
		init(skillId) {
			this.dialogVisible = true;
			// 等待DOM更新后重置表单
			this.$nextTick(() => {
				this.$refs.form.resetFields();
				// 设置skillId
				this.form.skillId = skillId;
				// 加载签约级别和人员配置数据
				this.getSignLevel();
				this.getUserConfig();
			});
		},

		/**
		 * 关闭弹窗
		 */
		handleClose() {
			this.dialogVisible = false;
			this.submitLoading = false;
		},

		/**
		 * 获取签约级别列表
		 * 从字典数据中获取contractLevel类型的选项
		 */
		getSignLevel() {
			sysDicDataPageList({
				page: 1,
				pageSize: 100,
				code: "contractLevel",
			}).then((res) => {
				this.signLevelList = res.data.list || [];
			});
		},

		/**
		 * 远程搜索角色
		 * @param {string} query - 搜索关键词
		 */
		remoteSearchRole(query) {
			// 清空搜索时返回空
			if (!query) {
				this.roleList = [];
				return;
			}
			this.roleLoading = true;
			sysRolePage({
				searchKey: query,
				page: 1,
				pageSize: 10,
			}).then((res) => {
				this.roleList = res.data?.list || [];
				this.roleLoading = false;
			});
		},

		/**
		 * 下拉框获取焦点时，加载默认角色列表
		 */
		handleSelectFocus() {
			// 如果角色列表为空，加载默认数据
			if (this.roleList.length === 0) {
				this.getRoleList("");
			}
		},

		/**
		 * 获取角色列表
		 * @param {string} ids - 角色ID列表（逗号分隔）
		 */
		getRoleList(ids) {
			sysRolePage({
				page: 1,
				pageSize: 10,
				ids: ids,
			}).then((res) => {
				this.roleList = res.data?.list || [];
			});
		},

		/**
		 * 获取人员配置详情
		 * 加载已有配置用于编辑
		 */
		getUserConfig() {
			courseSkillScopeInfo({ skillId: this.form.skillId }).then((res) => {
				if (res.code === "0" && res.data?.contractLevelIdList?.length > 0) {
					// 已有配置，填充数据
					this.form.contractLevelIdList = res.data.contractLevelIdList || [];
					this.form.roleIdList = res.data.roleIdList || [];
					// 根据已选角色ID加载角色列表
					this.getRoleList(res.data.roleIdList.join(","));
				} else {
					// 无配置，加载空列表
					this.getRoleList("");
				}
			});
		},

		/**
		 * 提交表单
		 * 使用debounce防抖，防止重复提交
		 */
		handleSubmit: debounce(function () {
			if (this.submitLoading) return;
			// 表单验证
			this.$refs.form.validate((valid) => {
				if (!valid) return;

				// 开启提交loading
				this.submitLoading = true;
				this.handleClose();
				courseSkillScopeAdd(this.form, true)
					.then(() => {
						this.$message.success("配置成功");
					})
					.finally(() => {
						this.submitLoading = false;
					});
			});
		}, 300),
	},
};
</script>

<style lang="scss" scoped>
.el-select {
	width: 100%;
}
</style>
